import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import attendanceService from '../../../services/attendanceService';
import attendanceBatchService from '../../../services/attendanceBatchService';
import NotificationToast from '../../../components/ui/NotificationToast';

const MonthlyAttendanceEntry = ({ employee, onSave }) => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [bulkStatus, setBulkStatus] = useState('present');
  const [bulkClockIn, setBulkClockIn] = useState('09:00');
  const [bulkClockOut, setBulkClockOut] = useState('18:00');

  // Parse API datetime as local wall-clock (no UTC conversion)
  const parseApiDateTime = (dateTimeStr, attendanceDate) => {
    if (!dateTimeStr) return '';
    const raw = String(dateTimeStr).trim();
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
    if (match) {
      return `${match[1]}T${match[2]}`;
    }
    const timeMatch = raw.match(/(\d{2}:\d{2})/);
    if (timeMatch && attendanceDate) {
      return `${attendanceDate}T${timeMatch[1]}`;
    }
    return '';
  };

  const getTimeFromStored = (stored) => {
    if (!stored) return '';
    if (stored.includes('T')) {
      return stored.split('T')[1]?.slice(0, 5) || '';
    }
    return stored.slice(0, 5);
  };

  // Get employee ID - handle different possible property names
  const getEmployeeId = () => {
    if (!employee) return null;
    const id = employee.id || employee.employee_id || employee.user_id || null;
    // Debug log (remove in production if needed)
    if (employee && !id) {
      console.warn('MonthlyAttendanceEntry: Employee object found but no ID property:', {
        employee,
        hasId: !!employee.id,
        hasEmployeeId: !!employee.employee_id,
        hasUserId: !!employee.user_id
      });
    }
    return id;
  };

  const employeeId = getEmployeeId();

  // Debug: Log when employee changes
  useEffect(() => {
    if (employee) {
      console.log('MonthlyAttendanceEntry: Employee prop changed:', {
        employee,
        employeeId,
        hasId: !!employee.id,
        hasEmployeeId: !!employee.employee_id,
        hasUserId: !!employee.user_id
      });
    }
  }, [employee, employeeId]);

  // Generate month options (last 12 months + current + next 3)
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    // Start from 12 months ago
    const startDate = new Date(currentYear, currentMonth - 11, 1);
    // End at 3 months in the future
    const endDate = new Date(currentYear, currentMonth + 3, 1);
    
    let date = new Date(startDate);
    while (date <= endDate) {
      const year = date.getFullYear();
      const month = date.getMonth();
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      const label = `${monthNames[month]} ${year}`;
      
      options.push({ value, label });
      date.setMonth(date.getMonth() + 1);
    }
    
    return options.reverse();
  };

  // Get all days in the selected month
  const getDaysInMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      days.push({
        day,
        date: dateStr,
        dayOfWeek,
        isWeekend,
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  };

  // Load existing attendance for the month
  const loadAttendanceForMonth = async () => {
    if (!employeeId) return;
    
    try {
      setLoading(true);
      const [year, month] = selectedMonth.split('-').map(Number);
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      
      const result = await attendanceService.getAttendanceRecords({
        employee_id: employeeId,
        date_from: startDate,
        date_to: endDate
      });
      
      if (result.success && result.data) {
        const data = {};
        result.data.forEach(record => {
          const date = record.attendance_date?.split('T')[0] || record.attendance_date;
          data[date] = {
            status: record.status || 'present',
            clock_in: parseApiDateTime(record.clock_in_time, date),
            clock_out: parseApiDateTime(record.clock_out_time, date),
            notes: record.notes || ''
          };
        });
        setAttendanceData(data);
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
      setNotification({ message: 'Failed to load attendance data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttendanceForMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, employeeId]);

  // Update attendance for a specific date (supports per-day edits after bulk apply)
  const updateDayAttendance = (date, field, value) => {
    setAttendanceData((prev) => {
      const existing = prev[date] || {};
      const next = { ...existing, [field]: value };

      if (field !== 'status' && !next.status) {
        next.status = 'present';
      }

      if (field === 'status') {
        if (value === 'absent') {
          next.clock_in = '';
          next.clock_out = '';
        }
      }

      return { ...prev, [date]: next };
    });
  };

  // All date strings (YYYY-MM-DD) in the selected month
  const getMonthDateStrings = () =>
    getDaysInMonth()
      .filter((dayInfo) => dayInfo !== null)
      .map((dayInfo) => dayInfo.date);

  // Bulk update days with status / clock in / clock out from Bulk Update fields
  const handleBulkUpdate = (selectedDates) => {
    const hasStatus = Boolean(bulkStatus);
    const hasClockIn = Boolean(bulkClockIn);
    const hasClockOut = Boolean(bulkClockOut);

    if (!hasStatus && !hasClockIn && !hasClockOut) {
      setNotification({ message: 'Please set at least one field to update', type: 'error' });
      return;
    }

    const updates = {};
    selectedDates.forEach((date) => {
      const existing = attendanceData[date] || {};
      updates[date] = {
        ...existing,
        notes: existing.notes || '',
      };
      if (hasStatus) {
        updates[date].status = bulkStatus;
      } else if (!updates[date].status) {
        updates[date].status = 'present';
      }
      if (hasClockIn) {
        updates[date].clock_in = `${date}T${bulkClockIn}`;
      }
      if (hasClockOut) {
        updates[date].clock_out = `${date}T${bulkClockOut}`;
      }
      if (updates[date].status === 'absent') {
        updates[date].clock_in = '';
        updates[date].clock_out = '';
      }
    });

    setAttendanceData((prev) => ({ ...prev, ...updates }));
    setNotification({ message: `Updated ${selectedDates.length} day(s)`, type: 'success' });
  };

  // Extract HH:MM from datetime-local or ISO string
  const extractTime = (dateTime) => {
    if (!dateTime) return null;
    if (dateTime.includes('T')) {
      const timePart = dateTime.split('T')[1];
      return timePart ? timePart.slice(0, 5) : null;
    }
    return dateTime.length >= 5 ? dateTime.slice(0, 5) : null;
  };

  // Save all attendance records
  const handleSaveAll = async () => {
    if (!employeeId) {
      setNotification({ message: 'Please select an employee', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      const records = Object.entries(attendanceData)
        .filter(([, data]) => data.status || data.clock_in || data.clock_out)
        .map(([date, data]) => ({
          attendance_date: date,
          clock_in_time: extractTime(data.clock_in),
          clock_out_time: extractTime(data.clock_out),
          status: data.status || 'present',
          notes: data.notes || ''
        }));

      if (records.length === 0) {
        setNotification({ message: 'No attendance data to save', type: 'error' });
        return;
      }

      const result = await attendanceBatchService.saveMonthly({
        employee_id: employeeId,
        records,
        entry_method: 'manual'
      });

      if (!result.success) {
        setNotification({
          message: result.error || 'Failed to save attendance data',
          type: 'error'
        });
        return;
      }

      const { success_count: successCount = 0, failed_count: errorCount = 0 } = result.data || {};

      if (errorCount > 0) {
        setNotification({
          message: `Saved ${successCount} record(s), ${errorCount} failed`,
          type: 'warning'
        });
      } else {
        setNotification({
          message: `Successfully saved ${successCount} attendance record(s)`,
          type: 'success'
        });
      }

      await loadAttendanceForMonth();

      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving attendance:', error);
      setNotification({ message: 'Failed to save attendance data', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const days = getDaysInMonth();
  const [year, month] = selectedMonth.split('-').map(Number);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthName = monthNames[month - 1];

  const statusOptions = [
    { value: 'present', label: 'Present' },
    { value: 'late', label: 'Late' },
    { value: 'absent', label: 'Absent' },
    { value: 'half_day', label: 'Half Day' },
    { value: 'early_departure', label: 'Early Departure' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'late':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'half_day':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'early_departure':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onDismiss={() => setNotification(null)}
          position="top-right"
          autoHideDuration={3000}
        />
      )}

      {/* Header with Month Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-card border border-border rounded-lg">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Monthly Attendance Entry</h3>
          <p className="text-sm text-muted-foreground">
            {employee?.full_name || employee?.name || employee?.user_profiles?.full_name || 'Select an employee'} - {monthName} {year}
          </p>
          {!employeeId && employee && (
            <p className="text-xs text-red-600 mt-1">
              ⚠️ Employee selected but ID not found. Please select employee from the table.
            </p>
          )}
          {!employee && (
            <p className="text-xs text-muted-foreground mt-1">
              Please select an employee from the table above
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedMonth}
            onValueChange={setSelectedMonth}
            options={generateMonthOptions()}
            className="w-48"
          />
          <Button
            onClick={handleSaveAll}
            disabled={saving || loading || !employeeId}
            iconName={saving ? "Loader2" : "Save"}
            iconPosition="left"
          >
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      {/* Bulk Update Section */}
      <div className="p-4 bg-muted/50 border border-border rounded-lg">
        <h4 className="text-sm font-semibold text-foreground mb-3">Bulk Update</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Status</label>
            <Select
              value={bulkStatus}
              onValueChange={setBulkStatus}
              options={[{ value: '', label: 'No Change' }, ...statusOptions]}
              placeholder="Select status"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Clock In</label>
            <Input
              type="time"
              value={bulkClockIn}
              onChange={(e) => setBulkClockIn(e.target.value)}
              placeholder="HH:MM"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Clock Out</label>
            <Input
              type="time"
              value={bulkClockOut}
              onChange={(e) => setBulkClockOut(e.target.value)}
              placeholder="HH:MM"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const monthDates = getMonthDateStrings();
                if (monthDates.length === 0) {
                  setNotification({ message: 'No days in selected month', type: 'error' });
                  return;
                }
                handleBulkUpdate(monthDates);
              }}
              className="w-full"
              disabled={!employeeId}
            >
              Apply to All
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Apply to All fills every day in the month. You can then edit individual days (Late, Absent, Half Day, etc.) before saving.
        </p>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground text-sm">Loading attendance data...</p>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((dayInfo, index) => {
              if (!dayInfo) {
                return <div key={`empty-${index}`} className="h-32"></div>;
              }

              const { day, date, isWeekend, isToday } = dayInfo;
              const dayData = attendanceData[date] || {};
              const hasData = dayData.status || dayData.clock_in || dayData.clock_out;

              return (
                <div
                  key={date}
                  className={`min-h-[140px] p-2 border rounded-lg ${
                    isWeekend ? 'bg-muted/30' : 'bg-card'
                  } ${isToday ? 'ring-2 ring-primary' : ''} ${
                    hasData ? getStatusColor(dayData.status || 'present') : 'border-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-foreground'}`}>
                      {day}
                    </span>
                    {hasData && (
                      <Icon name="CheckCircle" size={12} className="text-green-600" />
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <Select
                      value={dayData.status || 'present'}
                      onValueChange={(value) => updateDayAttendance(date, 'status', value)}
                      options={statusOptions}
                      placeholder="Status"
                      className="text-xs"
                    />
                    <Input
                      type="time"
                      value={getTimeFromStored(dayData.clock_in)}
                      onChange={(e) => {
                        const timeValue = e.target.value;
                        if (timeValue) {
                          updateDayAttendance(date, 'clock_in', `${date}T${timeValue}`);
                        } else {
                          updateDayAttendance(date, 'clock_in', '');
                        }
                      }}
                      placeholder="In"
                      className="text-xs"
                      disabled={dayData.status === 'absent'}
                    />
                    <Input
                      type="time"
                      value={getTimeFromStored(dayData.clock_out)}
                      onChange={(e) => {
                        const timeValue = e.target.value;
                        if (timeValue) {
                          updateDayAttendance(date, 'clock_out', `${date}T${timeValue}`);
                        } else {
                          updateDayAttendance(date, 'clock_out', '');
                        }
                      }}
                      placeholder="Out"
                      className="text-xs"
                      disabled={dayData.status === 'absent'}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/30 rounded-lg">
        <span className="text-sm font-medium text-foreground">Legend:</span>
        {statusOptions.map(status => (
          <div key={status.value} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded border ${getStatusColor(status.value)}`}></div>
            <span className="text-xs text-muted-foreground">{status.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthlyAttendanceEntry;

